// QR Kiosk v2 — KV2-8 guest session storage. Auth is stored via the SAME mechanism
// the kiosk already uses: accountContext.finishLogin → localStorage['accessToken'].
// The axios request interceptor then attaches it to every kiosk API call, and
// accountContext.user decodes role/name from it. We do NOT invent a parallel auth store.
//
// Alongside the token we persist a small DISPLAY-ONLY companion record (guest name,
// owner company, pass window, and — for magic-link redeems only — the redeem token so
// the banner can silently re-mint a lapsed JWT while the pass is still valid). This is
// metadata for the guest-pass banner, not a second credential.
import { jwtDecode } from "jwt-decode";
import accountContext from "../../../account/accountContext";
import { GuestRedeemResult } from "./guestAccessApi";

const GUEST_PASS_KEY = "kioskGuestPass";
export const GUEST_ROLE = "guest"; // lowercased comparison target

export interface GuestPassMeta {
  guestName: string;
  ownerCompanyName: string;
  machineInternalNumber: string;
  passExpiresAt: string; // ISO
  /** Magic-link token, kept OUT of the URL for silent re-redeem. Absent for PIN redeems. */
  redeemToken?: string;
}

/** Persist the guest token (existing kiosk mechanism) + the display companion record. */
export function storeGuestSession(result: GuestRedeemResult, redeemToken?: string): GuestPassMeta {
  accountContext.finishLogin({ tokenType: result.tokenType, accessToken: result.accessToken });
  const meta: GuestPassMeta = {
    guestName: result.guestName || "",
    ownerCompanyName: result.ownerCompanyName || "",
    machineInternalNumber: result.machineInternalNumber,
    passExpiresAt: result.passExpiresAt,
    redeemToken,
  };
  try {
    localStorage.setItem(GUEST_PASS_KEY, JSON.stringify(meta));
  } catch {
    /* storage full/blocked — banner degrades to token-only, non-fatal */
  }
  return meta;
}

export function readGuestPassMeta(): GuestPassMeta | null {
  try {
    const raw = localStorage.getItem(GUEST_PASS_KEY);
    return raw ? (JSON.parse(raw) as GuestPassMeta) : null;
  } catch {
    return null;
  }
}

/** Drop the guest token (existing mechanism) AND the companion record. */
export function clearGuestSession() {
  accountContext.logout();
  try {
    localStorage.removeItem(GUEST_PASS_KEY);
  } catch {
    /* ignore */
  }
}

export function isGuestRole(role: string | undefined | null): boolean {
  return (role || "").toLowerCase() === GUEST_ROLE;
}

/** Epoch-ms expiry of the JWT (the session, ≤12h), or null if undecodable. */
export function jwtExpMs(token: string | undefined): number | null {
  if (!token) return null;
  try {
    const decoded: any = jwtDecode(token);
    return typeof decoded?.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}
